import { TemplateRef } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { ClientTypeType } from './client-type.model';
import { Translation } from './translation';
import { Quote, QuoteType } from './quote.model';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { RoutedLabel } from './routed-label';

export interface ApplicationHooksData {
  deactivateDialog?: {
    title?: string;
    content: TemplateRef<any> | string;
    contentData?: any;
    confirmButtonText?: string;
    cancelButtonText?: string;
    disableUntilScrolledToBottom?: boolean;
  };
}

export interface ApplicationHooks<TMeta> {
  data: ApplicationHooksData;
  meta: TMeta;
  beforeInit: (proposal: Proposal, config: ApplicationConfig) => Proposal;
  afterInit: (proposal: Proposal, config: ApplicationConfig) => Proposal;
  beforePrint: (
    data: ApplicationData,
    config: ApplicationConfig,
    steps: ApplicationStep[]
  ) => ProposalPrintGroup[];
  beforeRelease?: (data: ApplicationData, form: FormGroup) => Promise<Proposal>;
  afterRelease?: (data: ApplicationData, form: FormGroup) => Promise<Proposal>;
  beforeReturn?: (data: ApplicationData, form: FormGroup) => Promise<Proposal>;
  afterReturn?: (data: ApplicationData, form: FormGroup) => Promise<Proposal>;
  beforeSave?: (
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ) => Promise<Proposal | void>;
  afterSave?: (
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ) => Proposal;
  beforeSubmit?: (
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ) => Promise<Proposal | void>;
  afterSubmit?: (
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ) => Proposal;
  beforeStepSubmit?: (
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ) => Promise<Proposal | void>;
  afterStepSubmit?: (
    data: ApplicationData,
    form: FormGroup,
    proposal: Proposal,
    config: ApplicationConfig
  ) => Proposal;
}

export enum ProposalSource {
  SignUp = 'sign-up',
  Import = 'import',
  MTA = 'mta',
  Broker = 'broker',
}

export interface Proposal {
  id: string;
  clientId: number;
  policyId: number | null;
  portalId: number;
  quoteId: number;
  lastUpdatedByName: string;
  lastUpdatedByUserId: number;
  lastUpdatedDate: string;
  formData: Application;
  createdByName: string;
  createdByUserId: string;
  createdDate: string;
  canEdit: boolean;
  selectedProducts: number[];
  subType?: string;
}

export interface Application {
  initialized: boolean;
  clientInitialized?: boolean;
  completedStep: number | null;
  steps: ApplicationStep[];
  data: ApplicationData;
  addons?: string[];
  referrals?: ProposalIssue[];
  source?: ProposalSource;
  permissions?: ProposalPermissions;
  version?: string;
}

export type ProposalPermissions = Record<
  ProposalPermissionType,
  Record<string, Record<string, ProposalPermission>>
>;

export interface ProposalPermissionsDef {
  type: ProposalPermissionType;
  source: string;
  target: string;
  validator(form: FormGroup): ProposalPermissionStatus;
}

export interface ProposalPermission {
  status: ProposalPermissionStatus;
  label?: string;
  routedLabel?: RoutedLabel[];
}

export enum ProposalPermissionStatus {
  Disabled,
  Enabled,
}

export type ProposalPermissionType = 'readonly';

export enum ApplicationStepType {
  Step = 'step',
  Template = 'template',
}

export interface ApplicationStep {
  id: string;
  state: ApplicationStepState;
  type?: ApplicationStepType;
  meta?: {
    iteratorLabel: string;
    indexOverride: number;
  };
}

export interface ApplicationStepUI extends ApplicationStep {
  label: string;
}

export enum ApplicationStepState {
  Uncompleted,
  InProgress,
  Completed,
  Invalid,
}

interface ApplicationConfigBase {
  id: string;
  portalId: number | string;
  locale: string;
  label: string;
  linearProgress: boolean;
  useDefaultStepIntro: boolean;
  version: string; // format: major.minor.fix
  /**
   @deprecated Used in LSOS only. For the proposals lib use steps instead
   */
  forms?: any[];
  partials?: (string | ApplicationPartialConfigStepOverride)[];
  templates?: Record<string, ApplicationConfigStep>;
}

export interface ApplicationConfig extends ApplicationConfigBase {
  steps: ApplicationConfigStep[];
}

export interface ApplicationConfigRaw extends ApplicationConfigBase {
  steps: (ApplicationConfigStep | ApplicationConfigStepPlaceholder)[];
}

interface ApplicationConfigStepPlaceholder {
  devNotes: string;
}

export interface ApplicationPartialConfig {
  id: string;
  portalId: number | '*';
  locale: string;
  steps?: ApplicationConfigStep[];
  groups?: ApplicationConfigFieldGroup[];
  templates?: Record<string, ApplicationConfigStep>;
}

export interface ApplicationPartialConfigStepOverride {
  id: string;
  overrides: {
    indexOverride?: number;
  };
  omitFields?: Record<string, string[]>;
  includeFields?: Record<string, string[]>;
}

export interface ApplicationPartialConfigGroupOverride {
  id: string;
  overrides: {
    label?: string;
    indexOverride?: number;
  };
}

export interface ApplicationConfigStep {
  id: string;
  label: string;
  fieldGroups: ApplicationConfigFieldGroup[];
  mandatory: boolean;
  productPicker: boolean;
  clientType: ClientTypeType;
  quoteType?: QuoteType[];
  intro?: ApplicationConfigIntro;
  guide?: ApplicationConfigGuide;
  meta?: ApplicationConfigMeta;
  partials?: (string | ApplicationPartialConfigGroupOverride)[];
  indexOverride?: number;
}

export interface ApplicationConfigFieldGroup {
  id: string;
  clientType?: ClientTypeType;
  label?: string;
  fields: any;
  partials?: string[];
  permissions?: ApplicationConfigPermissions;
}

export type ApplicationConfigPermissions = Record<
  ProposalPermissionType,
  ProposalPermission
>;

export interface ApplicationConfigIntro {
  title?: string;
  icon?: string;
  iconBackgroundColor?: string;
}

export interface ApplicationConfigGuide {
  title: string;
  content: string;
  icon?: string;
  color?: string;
}

export interface ApplicationConfigMeta {
  externalLink?: string;
}

export interface Addon {
  id: string;
  name: string;
}

export interface AddonMeta {
  icon: string;
}

export type ApplicationData = Record<string, any>;

export type ProposalStepCustomizer = (
  dueToRenewal: ApplicationConfigStep[],
  productPicker: ApplicationConfigStep | undefined,
  addons: ApplicationConfigStep[],
  mandatory: ApplicationConfigStep[]
) => ApplicationConfigStep[];

export interface ProposalSaveEvent extends ProposalUpdateEvent {
  isValid: boolean;
  isAuto: boolean;
}

export interface ProposalQuoteReleaseEvent {
  quoteId: number;
}

export interface ProposalSubmitEvent {
  proposal: Proposal;
  quote?: Quote;
  confirmationRequired?: boolean;
}

export interface ProposalUpdateEvent {
  affectedStepsIds: Set<string>;
  proposal: Proposal;
  linearProgress: boolean;
  stepIndex: number;
}

export interface ProposalReturnEvent {
  quote: Quote;
}

export interface ProposalQuickRaterQuotePayload {
  portalId: number;
  clientId: number;
  classOfBusinessId: number;
  formData: Record<string, unknown>;
  meta?: {
    [key: string]: any;
  };
}

export interface ProposalQuickRaterQuoteRaw {
  clientId: number;
  classOfBusinessId: number;
  premium: number;

  grossPremium?: number;
  ipt?: number;
  adjustment?: number;
}

export interface ProposalQuickRaterQuote extends ProposalQuickRaterQuoteRaw {
  meta: {
    payload: any;
    valid?: boolean;
    [key: string]: any;
  };
}

export type ProposalQuickRaterQuotes = Record<string, ProposalQuickRaterQuote>;

export interface ProposalIssue {
  path: string;
  reason: Translation;
  resolved: boolean;
}

export interface ProposalIssueUI extends ProposalIssue {
  label: string;
  stepLabel: string;
  stepUrl: unknown[];
}

export interface ProposalPrintGroup {
  id: string;
  label: string;
  fields: FormlyFieldConfig[];
}
